import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'

import PlacowkaService from '@/services/PlacowkaService'
import OsobaService from '@/services/OsobaService'
import RozmowaService from '@/services/RozmowaService'
import ProjectsService from '@/services/ProjectsService'
import MiastoFilterModal from '@/filter-modals/components/MiastoFilterModal'
import GminaFilterModal from '@/filter-modals/components/GminaFilterModal'
import PowiatFilterModal from '@/filter-modals/components/PowiatFilterModal'
import WojewodztwoFilterModal from '@/filter-modals/components/WojewodztwoFilterModal'
import TypFilterModal from '@/filter-modals/components/TypFilterModal'
import usePlacowkiModal from '../list/hooks/usePlacowkiModal'
import PlacowkiModal from '../list/components/PlacowkiModal'
import useOsobaModal from '../../Osoba/list/hooks/useOsobaModal'
import OsobaModal from '../../Osoba/list/components/OsobaModal'
import PlacowkaFilterModal from '@/filter-modals/components/PlacowkaFilterModal'
import CommunicationHistoryCard from './CommunicationHistoryCard'
import PlacowkaDetailsCard from './PlacowkaDetailsCard'
import KategoriaKlienta from './KategoriaKlienta.jsx'
import PlacowkaPeopleCard from './PlacowkaPeopleCard'
import PlacowkaProjectsCard from './PlacowkaProjectsCard'
import PlacowkaKampanieCard from './PlacowkaKampanieCard'

const getPlacowkaId = value => {
	if (!value) return null
	if (typeof value === 'object') return value?._id || value?.id || null
	return value
}

export default function PlacowkaDetails() {
	const { id } = useParams()
	const navigate = useNavigate()
	const [placowka, setPlacowka] = useState(null)
	const [people, setPeople] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [rozmowy, setRozmowy] = useState([])
	const [placowkaProjects, setPlacowkaProjects] = useState([])
	const [refreshKey, setRefreshKey] = useState(0)

	const reloadDetails = useCallback(() => {
		setRefreshKey(k => k + 1)
	}, [])

	const {
		isModalOpen,
		editingItem,
		formName,
		setFormName,
		formAdres,
		setFormAdres,
		formMiastoId,
		formMiastoName,
		formGminaId,
		formGminaName,
		formPowiatId,
		formPowiatName,
		formWojewodztwoId,
		formWojewodztwoName,
		formTypId,
		formTypName,
		formNip,
		setFormNip,
		isSaving,
		isMiastoSelectOpen,
		isGminaSelectOpen,
		isPowiatSelectOpen,
		isWojewodztwoSelectOpen,
		isTypSelectOpen,
		openEditModal,
		closeModal,
		handleSubmit,
		openMiastoSelectModal,
		closeMiastoSelectModal,
		applyMiastoSelection,
		openGminaSelectModal,
		closeGminaSelectModal,
		applyGminaSelection,
		openPowiatSelectModal,
		closePowiatSelectModal,
		applyPowiatSelection,
		openWojewodztwoSelectModal,
		closeWojewodztwoSelectModal,
		applyWojewodztwoSelection,
		openTypSelectModal,
		closeTypSelectModal,
		applyTypSelection,
	} = usePlacowkiModal(reloadDetails)

	const {
		isModalOpen: isOsobaModalOpen,
		editingItem: editingOsoba,
		formImieINazwisko,
		setFormImieINazwisko,
		formFunkcja,
		setFormFunkcja,
		formTelefon,
		setFormTelefon,
		formEmail,
		setFormEmail,
		formPlacowkaId: formOsobaPlacowkaId,
		formPlacowkaName: formOsobaPlacowkaName,
		isSaving: isOsobaSaving,
		isPlacowkaSelectOpen: isOsobaPlacowkaSelectOpen,
		openCreateModal: openOsobaCreateModal,
		closeModal: closeOsobaModal,
		handleSubmit: handleOsobaSubmit,
		openPlacowkaSelectModal: openOsobaPlacowkaSelectModal,
		closePlacowkaSelectModal: closeOsobaPlacowkaSelectModal,
		applyPlacowkaSelection: applyOsobaPlacowkaSelection,
	} = useOsobaModal(reloadDetails)

	useEffect(() => {
		let alive = true

		const loadDetails = async () => {
			if (!id) {
				setPlacowka(null)
				setPeople([])
				setRozmowy([])
				setPlacowkaProjects([])
				setLoading(false)
				return
			}

			setLoading(true)
			setError(null)
			try {
				const [placowkaRes, osobyRes, rozmowyRes, projectsRes] = await Promise.all([
					PlacowkaService.getById(id),
					OsobaService.getByPlacowkaId(id),
					RozmowaService.getByPlacowkaId(id),
					ProjectsService.getAll({
						limit: 999,
						sortColumn: 'created',
						sortDirection: 'desc',
						filters: { placowkaId: id },
					}),
				])

				if (!alive) return

				if (placowkaRes?.error) {
					throw new Error(placowkaRes.error)
				}

				if (osobyRes?.error) {
					throw new Error(osobyRes.error)
				}

				const placowkaData = placowkaRes?.data || null
				const osoby = Array.isArray(osobyRes?.data) ? osobyRes.data : []
				const filteredPeople = osoby.filter(item => String(getPlacowkaId(item?.id_placowka)) === String(id))
				const rozmowyList = Array.isArray(rozmowyRes?.data) ? rozmowyRes.data : []
				const placowkaProjectsList = Array.isArray(projectsRes?.data) ? projectsRes.data : []

				setPlacowka(placowkaData)
				setPeople(filteredPeople)
				setRozmowy(rozmowyList)
				setPlacowkaProjects(placowkaProjectsList)
			} catch (err) {
				if (!alive) return
				setError(err.message || 'Nie udalo sie zaladowac szczegolow placowki.')
				setPlacowka(null)
				setPeople([])
				setRozmowy([])
				setPlacowkaProjects([])
			} finally {
				if (alive) setLoading(false)
			}
		}

		loadDetails()
		return () => {
			alive = false
		}
	}, [id, refreshKey])

	const handleEditClick = () => {
		if (placowka) openEditModal(placowka)
	}

	const handleDeleteClick = async () => {
		const placowkaId = placowka?._id || placowka?.id || id
		if (!placowkaId) return

		const displayName = placowka?.nazwa || '-'

		const confirm = await Swal.fire({
			title: 'Czy na pewno?',
			text: `Usunac placowke "${displayName}"?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Tak, usun',
			cancelButtonText: 'Anuluj',
		})
		if (!confirm.isConfirmed) return

		const result = await PlacowkaService.delete(placowkaId)
		if (!result?.success) {
			Swal.fire({
				title: 'Error',
				text: result?.error || 'Nie udalo sie usunac placowki',
				icon: 'error',
			})
			return
		}

		navigate('/klienci/placowki')
	}

	if (loading) {
		return <div className="min-h-screen flex items-center justify-center">Ladowanie szczegolow placowki...</div>
	}

	if (error) {
		return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>
	}

	if (!id) {
		return <div className="min-h-screen flex items-center justify-center">Nie wybrano placowki.</div>
	}

	return (
		<div>
			<div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto] lg:items-start">
				<PlacowkaDetailsCard placowka={placowka} onEdit={handleEditClick} onDelete={handleDeleteClick} />
				<KategoriaKlienta clientId={id} initialTriage={placowka?.triage} />
			</div>

			<ul role="list" className="grid grid-cols-1 gap-6 xl:grid-cols-2">
				<CommunicationHistoryCard rozmowy={rozmowy} placowka={placowka} />
				<PlacowkaPeopleCard people={people} placowka={placowka} onAdd={openOsobaCreateModal} />
				<PlacowkaProjectsCard projects={placowkaProjects} placowka={placowka} />
				<PlacowkaKampanieCard placowkaId={id} />
			</ul>

			<OsobaModal
				isOpen={isOsobaModalOpen}
				editingItem={editingOsoba}
				formImieINazwisko={formImieINazwisko}
				setFormImieINazwisko={setFormImieINazwisko}
				formFunkcja={formFunkcja}
				setFormFunkcja={setFormFunkcja}
				formTelefon={formTelefon}
				setFormTelefon={setFormTelefon}
				formEmail={formEmail}
				setFormEmail={setFormEmail}
				formPlacowkaId={formOsobaPlacowkaId}
				formPlacowkaName={formOsobaPlacowkaName}
				onOpenPlacowkaSelect={openOsobaPlacowkaSelectModal}
				isSaving={isOsobaSaving}
				onClose={closeOsobaModal}
				onSubmit={handleOsobaSubmit}
			/>
			<PlacowkaFilterModal
				isOpen={isOsobaPlacowkaSelectOpen}
				onClose={closeOsobaPlacowkaSelectModal}
				selectedPlacowkaId={formOsobaPlacowkaId}
				onApply={applyOsobaPlacowkaSelection}
				title="Wybierz placowke"
				description="Wyszukaj i wybierz placowke dla osoby."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>

			<PlacowkiModal
				isOpen={isModalOpen}
				editingItem={editingItem}
				formName={formName}
				setFormName={setFormName}
				formAdres={formAdres}
				setFormAdres={setFormAdres}
				formMiastoId={formMiastoId}
				formMiastoName={formMiastoName}
				onOpenMiastoSelect={openMiastoSelectModal}
				formGminaId={formGminaId}
				formGminaName={formGminaName}
				onOpenGminaSelect={openGminaSelectModal}
				formPowiatId={formPowiatId}
				formPowiatName={formPowiatName}
				onOpenPowiatSelect={openPowiatSelectModal}
				formWojewodztwoId={formWojewodztwoId}
				formWojewodztwoName={formWojewodztwoName}
				onOpenWojewodztwoSelect={openWojewodztwoSelectModal}
				formTypId={formTypId}
				formTypName={formTypName}
				onOpenTypSelect={openTypSelectModal}
				formNip={formNip}
				setFormNip={setFormNip}
				isSaving={isSaving}
				onClose={closeModal}
				onSubmit={handleSubmit}
			/>
			<MiastoFilterModal
				isOpen={isMiastoSelectOpen}
				onClose={closeMiastoSelectModal}
				selectedMiastoId={formMiastoId}
				idGmina={formGminaId}
				idPowiat={formPowiatId}
				idWojewodztwo={formWojewodztwoId}
				onApply={applyMiastoSelection}
				title="Wybierz miasto"
				description="Wyszukaj i wybierz miasto dla placowki."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<GminaFilterModal
				isOpen={isGminaSelectOpen}
				onClose={closeGminaSelectModal}
				selectedGminaId={formGminaId}
				idPowiat={formPowiatId}
				idWojewodztwo={formWojewodztwoId}
				onApply={applyGminaSelection}
				title="Wybierz gmine"
				description="Wyszukaj i wybierz gmine dla placowki."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<PowiatFilterModal
				isOpen={isPowiatSelectOpen}
				onClose={closePowiatSelectModal}
				selectedPowiatId={formPowiatId}
				idWojewodztwo={formWojewodztwoId}
				onApply={applyPowiatSelection}
				title="Wybierz powiat"
				description="Wyszukaj i wybierz powiat dla placowki."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<WojewodztwoFilterModal
				isOpen={isWojewodztwoSelectOpen}
				onClose={closeWojewodztwoSelectModal}
				selectedWojewodztwoId={formWojewodztwoId}
				onApply={applyWojewodztwoSelection}
				title="Wybierz wojewodztwo"
				description="Wyszukaj i wybierz wojewodztwo dla placowki."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<TypFilterModal
				isOpen={isTypSelectOpen}
				onClose={closeTypSelectModal}
				selectedTypId={formTypId}
				onApply={applyTypSelection}
				title="Wybierz typ placowki"
				description="Wyszukaj i wybierz typ placowki."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
		</div>
	)
}
